// @flow
import BaseLayoutHintRule from "./BaseLayoutHintRule";

import {
  DEPENDENT_ATTRIBUTE_CONTROL,
  DEPENDENT_ATTRIBUTE,
} from "../../../constants";

import ChoiceAttributeModel from "../ChoiceAttributeModel";
import BooleanAttributeModel from "../BooleanAttributeModel";

import { ConfigurationException } from "../../../exceptions";

import type AttributeCollection from "../AttributeCollection";
import type AttributeModel from "../AttributeModel";

const EQUALS = "equals";
const INCLUDES = "includes";
const NOT_EQUALS = "notEquals";
const NOT_INCLUDES = "notIncludes";
const NOT_IS_EMPTY = "notIsEmpty";
const IS_EMPTY = "isEmpty";

const SHOW = "show";
const HIDE = "hide";

/**
 * Depending attributes hint logic
 */
class DependentAttribute extends BaseLayoutHintRule {
  _action: string;
  _control: string;
  _operator: string;
  _options: Array<string>;

  /**
   */
  static checkHintApplicability: boolean = true;

  /**
   */
  static isApplicableForHint(hint: string): boolean {
    const regex = new RegExp(
      `^${DEPENDENT_ATTRIBUTE}\\s?(${SHOW}|${HIDE})`,
      "i",
    );

    return regex.test(hint);
  }

  /**
   * Used on initHint to create an instance of this rule
   */
  static getRuleInstance(): DependentAttribute {
    return new DependentAttribute();
  }

  /**
   */
  static initHint(hint: string): any {
    const rule = this.getRuleInstance();

    const OPERATORS = `${EQUALS}|${INCLUDES}|${NOT_EQUALS}|${NOT_INCLUDES}|${NOT_IS_EMPTY}|${IS_EMPTY}`;
    const regex = new RegExp(
      `${DEPENDENT_ATTRIBUTE}\\s?(\\S*)\\swhen ${DEPENDENT_ATTRIBUTE_CONTROL}:\\s?(.*?)\\s*(${OPERATORS})\\s(.*)`,
      "i",
    );
    const isEmpyNotIsEmptRegex = new RegExp(
      `${DEPENDENT_ATTRIBUTE}\\s?(\\S*)\\swhen ${DEPENDENT_ATTRIBUTE_CONTROL}:\\s?(.*?)\\s*(${OPERATORS})`,
      "i",
    );

    const result = hint.match(regex)
      ? hint.match(regex)
      : hint.match(isEmpyNotIsEmptRegex);

    if (result) {
      rule.action = result[1];
      rule.control = result[2];
      rule.operator = result[3];
      rule.options = !result[4] ? "" : result[4];
    } else {
      throw new ConfigurationException(
        `No dependent information found on layout hint: ${hint}`,
      );
    }

    return rule;
  }

  /**
   */
  get action(): string {
    return this._action;
  }

  /**
   */
  set action(action: string) {
    this._action = action;
  }

  /**
   */
  get control(): string {
    return this._control;
  }

  /**
   */
  set control(control: string) {
    this._control = control;
  }

  /**
   */
  get operator(): string {
    return this._operator;
  }

  /**
   */
  set operator(operator: string) {
    this._operator = operator;
  }

  /**
   */
  get options(): Array<string> {
    return this._options;
  }

  /**
   */
  set options(options: string): void {
    this._options = options.split("|").map((option) => option.trim());
  }

  /**
   */
  processEqualsOptions(
    controlAttribute: ChoiceAttributeModel | BooleanAttributeModel,
  ): boolean {
    if (
      controlAttribute instanceof ChoiceAttributeModel ||
      controlAttribute instanceof BooleanAttributeModel
    ) {
      return this.options.every((option) =>
        controlAttribute.selected.includes(option),
      );
    }

    throw new ConfigurationException(
      "Dependent control in combination with equals operator must be a choice, string or number attribute",
    );
  }

  /**
   */
  processIncludesOptions(
    controlAttribute: ChoiceAttributeModel | BooleanAttributeModel,
  ): boolean {
    if (controlAttribute instanceof ChoiceAttributeModel) {
      return this.options.some((option) =>
        controlAttribute.selected.includes(option),
      );
    }

    throw new ConfigurationException(
      "Dependent control in combination with includes operator must be a choice attribute",
    );
  }

  /**
   */
  handleOperator(
    controlAttribute: ChoiceAttributeModel | BooleanAttributeModel,
  ): boolean {
    switch (this.operator) {
      case EQUALS:
        return this.processEqualsOptions(controlAttribute);
      case INCLUDES:
        return this.processIncludesOptions(controlAttribute);
      case NOT_EQUALS:
        return !this.processEqualsOptions(controlAttribute);
      case NOT_INCLUDES:
        return !this.processIncludesOptions(controlAttribute);
      case NOT_IS_EMPTY:
        return controlAttribute?.selected.length !== 0;
      case IS_EMPTY:
        return controlAttribute?.selected.length === 0;
      default:
        throw new ConfigurationException(
          `Unsupported operator for dependent question: ${this.operator}`,
        );
    }
  }

  /**
   */
  getControlAttribute(
    attributes: AttributeCollection,
  ): ChoiceAttributeModel | BooleanAttributeModel | null {
    const attribute = attributes.getAttributeByLayoutHint(
      `${DEPENDENT_ATTRIBUTE_CONTROL}:${this.control}`,
      `${DEPENDENT_ATTRIBUTE_CONTROL}: ${this.control}`,
    );

    if (
      attribute instanceof ChoiceAttributeModel ||
      attribute instanceof BooleanAttributeModel
    ) {
      return attribute;
    }

    return null;
  }

  /**
   */
  process(attribute: AttributeModel, attributes: AttributeCollection): void {
    const controlAttribute = this.getControlAttribute(attributes);
    if (!controlAttribute) {
      return;
    }

    controlAttribute.processLayoutHintRules(attributes);
    if (controlAttribute.isHidden) {
      attribute.hide();
      return;
    }

    const result = this.handleOperator(controlAttribute);
    if (this.action === SHOW) {
      attribute.isHidden = !result;
    } else if (this.action === HIDE) {
      attribute.isHidden = result;
    } else {
      throw new ConfigurationException(
        `Unsupported action ${this.action} for dependent question on attribute: ${attribute.label}`,
      );
    }
  }
}

export default DependentAttribute;
